import { useEffect, useRef, useState } from 'react';
import type {
  PaymentRequest,
  PaymentRequestOptions,
  PaymentRequestPaymentMethodEvent,
} from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe';

export type SheetCadence = 'monthly' | 'quarterly' | 'sixMonth' | 'twelveMonth';

/** What a sheet shows: its lines, total and any subscription description. */
export type WalletSheet = Pick<PaymentRequestOptions, 'total' | 'displayItems' | 'applePay'>;

export interface SheetLine {
  title: string;
  cadence: SheetCadence;
  /** Charged today for this line. */
  firstCents: number;
  /** Charged at every renewal (member price where it applies). */
  renewalCents: number;
}

// Plans renew in 28-day cycles.
const CADENCE_DAYS: Record<SheetCadence, number> = { monthly: 28, quarterly: 84, sixMonth: 168, twelveMonth: 336 };
const CADENCE_LABEL: Record<SheetCadence, string> = { monthly: 'monthly', quarterly: 'quarterly', sixMonth: '6-month', twelveMonth: '12-month' };

/** What the Apple Pay sheet shows: every line with its plan and the
 * discounts, adding up to today's total. When everything renews on one
 * schedule it also describes the subscription (today's total for the first
 * period, then the renewal), which makes Apple issue a merchant token instead
 * of a one-time device token. A sheet holds one renewal schedule, so a cart
 * mixing plans only lists its lines. */
export function applePaySheet(
  lines: SheetLine[],
  discounts: { label: string; cents: number }[],
  totalCents: number,
  // False when a promo code is applied: the portal can't tell whether it also
  // discounts renewals, so the sheet doesn't state a renewal amount.
  renewalKnown = true,
) {
  const sheet = {
    total: { label: 'System Labs', amount: Math.max(totalCents, 50) },
    displayItems: [
      ...lines.map((line) => ({ label: `${line.title} · ${CADENCE_LABEL[line.cadence]}`, amount: line.firstCents })),
      ...discounts.filter((d) => d.cents > 0).map((d) => ({ label: d.label, amount: -d.cents })),
    ],
  };
  if (!renewalKnown || !lines.length || new Set(lines.map((line) => line.cadence)).size !== 1) return sheet;
  const renewal = lines.reduce((sum, line) => sum + line.renewalCents, 0);
  if (!renewal) return sheet;
  const days = CADENCE_DAYS[lines[0].cadence];
  const name = lines.length === 1 ? lines[0].title : 'System Labs plan';
  const renewsOn = new Date();
  renewsOn.setDate(renewsOn.getDate() + days);
  const every = { recurringPaymentIntervalUnit: 'day' as const, recurringPaymentIntervalCount: days };
  const intro = totalCents !== renewal;
  return {
    ...sheet,
    applePay: {
      recurringPaymentRequest: {
        paymentDescription: `${name} subscription`,
        managementURL: `${window.location.origin}/app/account?focus=subscriptions`,
        // Only future dates: Stripe rejects any that has passed.
        ...(intro
          // Apple shows the period ("28 days") beside it; a longer label wraps.
          ? { trialBilling: { amount: totalCents, label: 'Intro price', recurringPaymentEndDate: renewsOn, ...every } }
          : {}),
        regularBilling: {
          amount: renewal,
          label: name,
          ...(intro ? { recurringPaymentStartDate: renewsOn } : {}),
          ...every,
        },
      },
    },
  };
}

/** An Apple Pay sheet ready for a confirm button. Apple Pay must open straight
 * from the tap, so the sheet is built ahead, and rebuilt whenever its contents
 * change (an update can't take a subscription description back off a sheet).
 * Only the sheet built for the current contents opens: while a rebuild is in
 * flight `available` stays true (keep the Apple Pay button) but `show()`
 * returns false, so the tap waits instead of approving outdated terms. */
export function useApplePaySheet({
  enabled,
  publishableKey,
  sheet,
  onPaymentMethod,
}: {
  enabled: boolean;
  publishableKey: string | null;
  sheet: WalletSheet;
  onPaymentMethod: (event: PaymentRequestPaymentMethodEvent) => void;
}) {
  const [built, setBuilt] = useState<{ request: PaymentRequest; sheet: unknown } | null>(null);
  const [available, setAvailable] = useState(false);
  const showing = useRef(false);
  const handler = useRef(onPaymentMethod);
  handler.current = onPaymentMethod;
  useEffect(() => {
    if (!enabled || !publishableKey || showing.current) return;
    let cancelled = false;
    void getStripe(publishableKey).then(async (stripe) => {
      if (!stripe || cancelled) return;
      const next = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        disableWallets: ['googlePay', 'link'],
        ...sheet,
      });
      const can = await next.canMakePayment().catch(() => null);
      if (cancelled) return;
      setAvailable(Boolean(can?.applePay));
      if (!can?.applePay) return;
      next.on('paymentmethod', (event) => handler.current(event));
      next.on('cancel', () => { showing.current = false; });
      setBuilt({ request: next, sheet });
    });
    return () => { cancelled = true; };
  }, [enabled, publishableKey, sheet]);
  const current = enabled && built?.sheet === sheet ? built.request : null;
  return {
    /** Apple Pay applies here and the device supports it. */
    available: enabled && available,
    /** Call straight from the tap; false while the current sheet is still being built. */
    show() {
      if (!current) return false;
      showing.current = true;
      current.show();
      return true;
    },
    /** The sheet's payment finished (or was abandoned). */
    done() { showing.current = false; },
  };
}
